import type { Military, Soldier } from './military';
import type { NavalShip } from './naval';
import type { NavalLanding } from '../world/strategic-transport';
import type { WalkNode } from './walking';

interface Crew {
  soldier: Soldier;
  seat: number;
  state: 'staging' | 'boarding' | 'aboard' | 'ashore' | 'incapacitated';
  token: string;
  retry: number;
  inspected: boolean;
  queueNode?: number;
}

/** Twelve of the existing defenders start on their assigned naval duty.
 * Originals sail, unload, inspect the supported berth and walk back aboard
 * the same craft. Mobilisation preserves operators and in-progress boarding. */
export class MilitaryMaritime {
  crew: Crew[] = [];
  ship?: NavalShip;
  phase: 'boarding' | 'sailing' | 'inspecting' | 'mobilised' | 'unavailable' = 'unavailable';
  berth = 0;
  visits = 0;
  checks = 0;
  reason = 'Naval route unavailable';
  private age = 0;
  private serial = 0;
  private staging = new Map<NavalLanding, WalkNode[]>();
  constructor(readonly military: Military) {}
  clear(): void {
    this.crew = [];
    this.ship = undefined;
    this.phase = 'unavailable';
    this.berth = this.visits = this.checks = this.age = this.serial = 0;
    this.staging.clear();
  }
  *resetSteps(): Generator<void> {
    this.clear();
    const { population, city } = this.military,
      naval = population.naval;
    if (!naval || !city.strategic?.transport) return;
    const home = city.strategic.transport.landings[0];
    this.ship = naval.create('garrison-coastal-inspection', 'friendly', home.berth);
    if (!this.ship) return;
    const seats = [3, 4, 11, 12, 19, 20, 27, 28, 35, 36, 43, 44];
    for (let i = 0; i < seats.length; i++) {
      const soldier = this.military.friendly[514 + i];
      soldier.assignment = 'naval';
      soldier.role = i < 2 ? 'naval operator' : 'coastal inspection';
      population.civil?.cancelTask(soldier.person, 'Original naval assignment at world initialisation');
      // Initial-world placement, like initial road drivers and room occupants.
      // Neither mobilisation nor later trips use this placement path.
      soldier.person.p.copy(naval.seat(this.ship, seats[i]));
      soldier.person.previous.copy(soldier.person.p);
      soldier.person.velocity.set(0, 0, 0);
      if (!naval.passengers.attach(this.ship, soldier.person, seats[i]))
        throw new Error(`Original naval duty has no supported seat: ${soldier.id}`);
      soldier.state = 'aboard';
      this.crew.push({ soldier, seat: seats[i], state: 'aboard', token: '', retry: 0, inspected: false });
      yield;
    }
    this.ship.pilots = this.crew.slice(0, 2).map((c) => c.soldier.id);
    this.phase = 'boarding';
    this.reason = 'Original on-duty crew aboard at the garrison naval pier';
  }
  private healthy(c: Crew): boolean {
    return (
      !c.soldier.person.fatal &&
      !['incapacitated', 'falling', 'carried', 'recovering'].includes(c.soldier.person.state)
    );
  }
  private task(c: Crew, node: WalkNode, action: string, duration: number): boolean {
    const civil = this.military.population.civil!,
      token = `naval-duty:${c.soldier.id}:${++this.serial}`;
    if (!civil.assignTask(c.soldier.person, civil.nodePlace(node), token, action, duration)) return false;
    c.token = token;
    c.soldier.task = token;
    return true;
  }
  private candidates(landing: NavalLanding): WalkNode[] {
    let nodes = this.staging.get(landing);
    if (!nodes) {
      const network = this.military.population.civil!.network;
      nodes = network
        .near(landing.shore, 100, 'street')
        .filter((n) => Math.hypot(n.x - landing.door.x, n.z - landing.door.z) > 20)
        .sort(
          (a, b) =>
            Math.hypot(a.x - landing.shore.x, a.z - landing.shore.z) -
            Math.hypot(b.x - landing.shore.x, b.z - landing.shore.z),
        );
      this.staging.set(landing, nodes);
    }
    return nodes;
  }
  mobilise(): void {
    if (!this.ship) return;
    this.reason = 'Original naval team supporting mobilisation from its present position';
    const { population } = this.military;
    for (const c of this.crew)
      if (this.healthy(c) && c.state === 'ashore') {
        population.civil?.cancelTask(c.soldier.person, 'Naval crew joining local defence');
        c.soldier.state = 'deployed';
        c.soldier.person.controlled = true;
      }
    if (this.phase === 'inspecting') this.phase = 'mobilised';
  }
  availableForDefence(soldier: Soldier): boolean {
    return this.phase === 'mobilised' && this.crew.some((c) => c.soldier === soldier && c.state === 'ashore');
  }
  standDown(): void {
    if (!this.ship || this.ship.boat.state === 'wreck') return;
    this.phase = this.ship.route.length ? 'sailing' : 'boarding';
    for (const c of this.crew)
      if (this.healthy(c) && !c.soldier.person.carrier) {
        c.state = 'staging';
        c.token = '';
        c.inspected = false;
        c.retry = 0;
      }
  }
  update(dt: number): void {
    const { city, population, time } = this.military,
      naval = population.naval,
      ship = this.ship;
    if (!ship || !naval) return;
    this.age -= dt;
    if (this.age > 0) return;
    this.age = 0.2;
    if (ship.boat.state === 'wreck') {
      this.phase = 'unavailable';
      this.reason = 'Original naval transport wreck retained';
      return;
    }
    const landing = city.strategic!.transport!.landings[this.berth];
    for (const c of this.crew) {
      if (!this.healthy(c)) {
        c.state = 'incapacitated';
        continue;
      }
      const passenger = naval.passengers.people.get(c.soldier.id);
      if (passenger?.state === 'aboard') {
        c.state = 'aboard';
        c.soldier.state = 'aboard';
      } else if (passenger?.state === 'landed' && c.state === 'aboard') {
        c.state = 'ashore';
        c.token = '';
        c.soldier.state = 'deployed';
      }
    }
    if (this.phase === 'sailing' && ship.state === 'moored' && !ship.route.length) {
      if (
        naval.passengers.disembark(ship, landing, this.military.mobilised ? new Set(ship.pilots) : undefined)
      ) {
        this.phase = this.military.mobilised ? 'mobilised' : 'inspecting';
        this.visits++;
        this.reason = 'Original inspection team unloading at the actual coastal berth';
      }
    }
    if (this.phase === 'mobilised') {
      if (ship.state === 'moored' && this.crew.some((c) => c.state === 'aboard'))
        naval.passengers.disembark(ship, landing, new Set(ship.pilots));
      return;
    }
    if (this.phase === 'sailing' && ship.state === 'blocked' && naval.time - ship.blockedSince > 12) {
      const alternatives = city.strategic!.transport!.landings;
      const next = alternatives.find(
        (candidate, index) =>
          index !== this.berth &&
          !!this.military.physics.ground(candidate.door, 0.2, 0.3) &&
          !naval.ships.some(
            (other) =>
              other !== ship &&
              Math.hypot(other.boat.p.x - candidate.berth.x, other.boat.p.z - candidate.berth.z) < 40,
          ),
      );
      if (
        next &&
        naval.setRoute(
          ship,
          [
            { x: ship.boat.p.x, y: ship.boat.p.y, z: 805, reverse: ship.boat.p.z < 760 },
            { ...next.berth, z: 805 },
            next.berth,
          ],
          'Original naval crew diverting to an available supported berth',
        )
      ) {
        this.berth = alternatives.indexOf(next);
        this.reason = 'Blocked naval response using an available real pier';
      }
    }
    if (this.phase === 'sailing') return;
    const civil = population.civil!,
      candidates = this.candidates(landing);
    if (this.phase === 'inspecting') {
      for (const [i, c] of this.crew.entries()) {
        if (c.state !== 'ashore' || !this.healthy(c)) continue;
        const nearby = [...this.military.relief.alerts.values()].find(
          (a) => a.until > time && c.soldier.person.p.distanceTo(a.point) < 250,
        );
        if (nearby && population.services?.assistance.step(c.soldier.person, nearby.point, dt)) {
          this.reason =
            'Original naval crew assisting casualties and clearing loose rubble at the working pier';
          continue;
        }
        const task = civil.task(c.soldier.person);
        if (task?.token === c.token && task.state === 'completed') {
          if (!c.inspected) {
            c.inspected = true;
            this.checks++;
            c.soldier.checks++;
          }
        } else if (task?.token !== c.token || task.state === 'failed') {
          if (time >= c.retry && candidates[i])
            this.task(c, candidates[i], 'inspecting the coastal berth', 8);
          c.retry = time + 3;
        }
      }
      if (this.crew.every((c) => !this.healthy(c) || c.inspected)) {
        for (const c of this.crew)
          if (this.healthy(c)) {
            c.state = 'staging';
            c.token = '';
            c.retry = 0;
            c.inspected = false;
          }
        this.phase = 'boarding';
      }
    }
    if (this.phase !== 'boarding') return;
    const queue = this.crew.filter((c) => this.healthy(c) && !['aboard', 'boarding'].includes(c.state));
    const centre = landing.path.at(-2)!;
    for (const [i, c] of queue.entries()) {
      const p = c.soldier.person,
        task = civil.task(p),
        waiting = { x: centre.x, y: centre.y, z: centre.z - i * 1.75 },
        node = civil.network
          .near(waiting, i ? 1 : 0.4, 'street')
          .filter((n) => Math.abs(n.y - waiting.y) < 0.15)
          .sort(
            (a, b) =>
              Math.hypot(a.x - waiting.x, a.z - waiting.z) - Math.hypot(b.x - waiting.x, b.z - waiting.z),
          )[0];
      if (i === 0 && naval.passengers.board(ship, p, c.seat, landing)) {
        c.state = 'boarding';
        c.soldier.state = 'exiting';
        continue;
      }
      if (task?.token === c.token && task.state === 'active') continue;
      if (i > 0 && task?.token === c.token && task.state === 'completed' && node?.id === c.queueNode)
        continue;
      if (time < c.retry) continue;
      if (
        node &&
        this.task(c, node, i ? 'waiting for the naval gangway' : 'boarding the naval transport', 0.05)
      )
        c.queueNode = node.id;
      c.retry = time + 3;
    }
    if (this.crew.every((c) => !this.healthy(c) || c.state === 'aboard')) {
      const nextIndex = this.berth === 0 ? 1 : 0,
        next = city.strategic!.transport!.landings[nextIndex];
      if (
        naval.setRoute(
          ship,
          [{ ...landing.berth, z: 805, reverse: true }, { ...next.berth, z: 805 }, next.berth],
          'Crewed coastal inspection sailing to the next working pier',
        )
      ) {
        this.berth = nextIndex;
        this.phase = 'sailing';
        this.reason = 'Original crew aboard the coastal inspection service';
      }
    }
  }
  inspect(): unknown {
    return {
      phase: this.phase,
      reason: this.reason,
      berth: this.berth,
      visits: this.visits,
      checks: this.checks,
      ship: this.ship?.boat.id,
      crew: this.crew.map((c) => ({
        id: c.soldier.id,
        seat: c.seat,
        state: c.state,
        task: c.token,
        position: c.soldier.person.p.toArray(),
      })),
    };
  }
}
